import { Injectable } from '@angular/core';
import { KeyValue } from 'libs/core/src/lib/helper/key-value';
import { CinemaViewModel } from 'libs/core/src/lib/model/view-model/cinema/cinema.view.model';
import { RegionViewModel } from 'libs/core/src/lib/model/view-model/region/region.view.model';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CinemaAdapter, EventAdapter, MembershipAdapter, MovieAdapter, PackageAdapter, PostAdapter, RegionAdapter } from '../wp-model/adapters';
import { Like } from '../wp-model/like';
import { Membership } from '../wp-model/membership';
import { Package } from '../wp-model/package';
import { Post } from '../wp-model/post';
import { PostRequest } from '../wp-model/request/post-request';
import { SlideRequest } from '../wp-model/request/slide-request';
import { WordpressHttpService } from '../http/wordpress.http.service';
import { WeekdayViewModel } from '../wp-model/weekday';
import { MovieViewModel } from '../model/view-model/movie/movie.view.model';
import { EventViewModel } from '../model/view-model/event/event.view.model';
import { CustomOptions } from '../model/adapter';
import { WebComponentOptionsService } from '../service/webcomponent-options.service';
import { WpOptionEnum } from 'libs/webcomponent/src/lib/enum/wp-option.enum';

@Injectable({
  providedIn: 'root',
})
export class WordpressDataProvider {
  public movieAdapterTransformOptions: CustomOptions;
  transformNewLineToLineBreak = false;

  constructor(
    private httpService: WordpressHttpService,
    private membershipAdapter: MembershipAdapter,
    private regionAdapter: RegionAdapter,
    private cinemaAdapter: CinemaAdapter,
    private packageAdapter: PackageAdapter,
    private movieAdapter: MovieAdapter,
    private eventAdapter: EventAdapter,
    private postAdapter: PostAdapter,
    private webComponentOptionsService: WebComponentOptionsService
  ) {
    this.webComponentOptionsService.getWpOption$(WpOptionEnum.MOVIE_DESCRIPTION_ALLOW_NEW_LINE).subscribe((value) => {
      this.movieAdapterTransformOptions = {
        replaceNewLinesWithBr: Boolean(Number(value)),
      } as CustomOptions;
    });
  }

  public getPostsByCategories(categories: string, postRequest: PostRequest) {
    return this.httpService.getPostsByCategories(categories, postRequest);
  }

  public getPosts(postRequest: PostRequest, useLang = true) {
    return this.httpService.getPosts(postRequest, useLang);
  }

  public getPost(slug: string, type = WordpressPostTypeEnum.POSTS, useLang = true) {
    return this.httpService.getPost(slug, type, useLang);
  }

  public getRegions(): Observable<RegionViewModel[]> {
    return this.httpService.getRegions().pipe(
      map((data: Post[]) => data.map((item: Post) => this.regionAdapter.adapt(item))),
      map((regions) => regions.filter((o) => o.cinemaIds && o.cinemaIds.length))
    );
  }

  public getRegionById(id: string): Observable<RegionViewModel> {
    return this.getRegions().pipe(map((regions) => regions.find((o) => o.id === id)));
  }

  public getCinemas(): Observable<CinemaViewModel[]> {
    return this.httpService.getCinemas().pipe(map((data: Post[]) => data.map((item: Post) => this.cinemaAdapter.adapt(item))));
  }

  public getCinemaById(id: string): Observable<CinemaViewModel> {
    return this.getCinemas().pipe(map((cinemas) => cinemas.find((o) => o.id === id)));
  }

  public getPostMovieById(id: string) {
    return this.httpService.getPostMovieById(id);
  }

  public getMovies(): Observable<MovieViewModel[]> {
    return this.httpService.getMovies().pipe(map((items) => items.map((item) => this.movieAdapter.adapt(item, this.movieAdapterTransformOptions))));
  }

  public getMovieById(id: string) {
    return this.httpService.getMovieById(id).pipe(map((item) => this.movieAdapter.adapt(item, this.movieAdapterTransformOptions)));
  }

  public getMovieLikes(movieId: string) {
    return this.httpService.getLikes(movieId);
  }

  public getSuggestedMovies(movieId: string) {
    return this.httpService
      .getSuggestedMovies(movieId)
      .pipe(map((items) => items.map((item) => this.movieAdapter.adapt(item, this.movieAdapterTransformOptions))));
  }

  public getPromotedMovies() {
    return this.httpService.getPromotedMovies().pipe(
      map((items) => items.map((item) => this.movieAdapter.adapt(item, this.movieAdapterTransformOptions))),
      map((movie) => {
        return movie.filter((f) => f.promote_outside_schedule);
      })
    );
  }

  public getEventById(id: string) {
    return this.httpService.getEventById(id);
  }

  public getPromotedEvents() {
    return this.httpService.getPromotedEvents();
  }

  public toggleLike(id: string, isEvent = false): Observable<Like> {
    return this.httpService.toggleLike(id, isEvent);
  }

  public getMemberships(): Observable<Membership[]> {
    return this.httpService.getMemberships().pipe(map((items) => items.map((x) => this.membershipAdapter.adapt(x.custom_fields))));
  }

  public getConfig(): Observable<KeyValue<string>[]> {
    return this.httpService.getConfig();
  }

  public getSlider(slideRequest: SlideRequest) {
    return this.httpService.getSlider(slideRequest).pipe(map((data: any[]) => data.map((item) => this.postAdapter.adapt(item))));
  }

  public getPackages(): Observable<Package[]> {
    return this.httpService.getPackages().pipe(map((items) => items.map((x) => this.packageAdapter.adapt(x.custom_fields))));
  }

  public getProductsListById(id: string) {
    return this.httpService.getProductsListById(id);
  }

  public getAuditoriumById(id: string) {
    return this.httpService.getAuditoriumById(id);
  }

  public getEvents(): Observable<EventViewModel[]> {
    return this.httpService.getEvents().pipe(map((items) => items.map((item) => this.eventAdapter.adapt(item))));
  }

  public getEventLikes(eventId: string) {
    return this.httpService.getLikes(eventId, true);
  }

  public getWeekdays() {
    return this.httpService.getWeekdays().pipe(map((items) => items.map((item) => new WeekdayViewModel(item))));
  }
}

export enum WordpressPostTypeEnum {
  POSTS = 'posts',
  PRICE_TABLE = 'price-table',
  MOVIE = 'movie',
}
